import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Voicemail, Loader2, PhoneCall, PhoneMissed } from 'lucide-react';
import ClickToCallButton from '../../../components/ClickToCallButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

export default function CsVoicemailTab() {
  const [stats, setStats] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/cs-control/voicemails/pending`, { headers: authHeaders(), params: { days } }),
          axios.get(`${API}/cs-control/missed-recovery`, { headers: authHeaders(), params: { days } }),
        ]);
        setStats(r1.data);
        setRecovery(r2.data);
      } catch (_) { toast.error('Errore caricamento voicemail'); }
      setLoading(false);
    })();
  }, [days]);

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>;
  if (!stats) return null;

  return (
    <div className="space-y-4" data-testid="cs-voicemail-tab">
      <div className="flex items-center gap-2">
        {[7, 14, 30].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded-full text-sm border ${days === d ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
            {d} giorni
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Messaggi ricevuti</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="cs-vm-total">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Richiamati entro {stats.recall_hours}h</p>
          <p className="text-2xl font-bold text-emerald-600">{stats.recalled_within} <span className="text-sm text-slate-400">({stats.recalled_within_pct ?? '—'}%)</span></p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500 uppercase">Richiamati (in ritardo)</p>
          <p className="text-2xl font-bold text-amber-600">{Math.max((stats.recalled_any || 0) - (stats.recalled_within || 0), 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-rose-200 p-4 bg-rose-50/50">
          <p className="text-xs text-rose-600 uppercase font-semibold">Mai richiamati</p>
          <p className="text-2xl font-bold text-rose-600" data-testid="cs-vm-pending">{stats.pending.length}</p>
        </div>
      </div>

      {/* ─── Recupero chiamate perse ─── */}
      {recovery && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase">Numeri persi (unici)</p>
              <p className="text-2xl font-bold text-slate-800" data-testid="cs-rec-unique">{recovery.unique_numbers}</p>
              <p className="text-xs text-slate-400">{recovery.total_missed_calls} chiamate perse</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase">Richiamati dall'ufficio</p>
              <p className="text-2xl font-bold text-emerald-600" data-testid="cs-rec-office">
                {recovery.recalled_by_office} <span className="text-sm text-slate-400">({recovery.office_recall_pct ?? '—'}%)</span>
              </p>
              <p className="text-xs text-slate-400">di cui {recovery.recalled_within} entro {recovery.recall_hours}h</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase">Il cliente ha richiamato</p>
              <p className="text-2xl font-bold text-sky-600" data-testid="cs-rec-customer">{recovery.resolved_by_customer}</p>
              <p className="text-xs text-slate-400">e ha ricevuto risposta</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 uppercase">Recupero totale</p>
              <p className="text-2xl font-bold text-slate-800">{recovery.recovery_pct ?? '—'}%</p>
              <p className="text-xs text-slate-400">ufficio + cliente</p>
            </div>
            <div className="bg-white rounded-xl border border-rose-200 p-4 bg-rose-50/50">
              <p className="text-xs text-rose-600 uppercase font-semibold">Senza seguito</p>
              <p className="text-2xl font-bold text-rose-600" data-testid="cs-rec-unresolved">{recovery.unresolved}</p>
              <p className="text-xs text-rose-400">nessuno li ha ricontattati</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <PhoneMissed className="h-4 w-4 text-rose-500" />
              <h3 className="text-sm font-semibold text-slate-700">Chiamate perse senza alcun seguito — da richiamare</h3>
            </div>
            {recovery.pending.length === 0 ? (
              <p className="px-4 py-6 text-sm text-emerald-600">✅ Tutti i numeri persi sono stati ricontattati (o hanno richiamato)</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Ultima chiamata</th>
                    <th className="text-left px-3 py-2">Numero / Ospite</th>
                    <th className="text-right px-3 py-2">Tentativi</th>
                    <th className="text-center px-3 py-2">Fuori orario</th>
                  </tr>
                </thead>
                <tbody>
                  {recovery.pending.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`cs-rec-row-${i}`}>
                      <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                        {p.last_at ? new Date(p.last_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-700 flex items-center gap-1.5">{p.from_number} <ClickToCallButton number={p.from_number} /></p>
                        {p.guest && <p className="text-xs text-indigo-600">👤 {p.guest.guest_name} {p.guest.property_name ? `· ${p.guest.property_name}` : ''}</p>}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">{p.times_missed}</td>
                      <td className="px-3 py-2 text-center">{p.out_of_hours ? '🌙' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Voicemail className="h-4 w-4 text-rose-500" />
          <h3 className="text-sm font-semibold text-slate-700">Da richiamare (nessuna chiamata in uscita rilevata)</h3>
        </div>
        {stats.pending.length === 0 ? (
          <p className="px-4 py-6 text-sm text-emerald-600">✅ Tutti i messaggi in segreteria sono stati richiamati</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Data</th>
                <th className="text-left px-3 py-2">Numero / Ospite</th>
                <th className="text-left px-3 py-2">Messaggio</th>
              </tr>
            </thead>
            <tbody>
              {stats.pending.map((p, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`cs-vm-row-${i}`}>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                    {p.at ? new Date(p.at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-medium text-slate-700 flex items-center gap-1"><PhoneCall className="h-3.5 w-3.5 text-slate-400" /> {p.from_number} <ClickToCallButton number={p.from_number} /></p>
                    {p.guest && <p className="text-xs text-indigo-600">👤 {p.guest.guest_name} {p.guest.property_name ? `· ${p.guest.property_name}` : ''}</p>}
                  </td>
                  <td className="px-3 py-2">
                    {p.voicemail_url ? <audio controls preload="none" className="h-8 w-56" src={p.voicemail_url} /> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
